import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { ActiveDiningTable } from '../../Redux/ActiveStore/activeStoreSlice';

interface CheckoutRequestStatusBarProps {
  /** 稼働中テーブル。REQUESTED のものだけを拾う。 */
  activeTables: ActiveDiningTable[];
  /** テーブルを選択したとき（受領確認ポップアップを開く）。 */
  onSelect: (activeDiningTableId: string, tableName: string, paymentMethod: string | null) => void;
}

/** requestedAt からの経過を「◯分」で返す。1 分未満は「今」。不正な日時は null。 */
const formatWaiting = (requestedAt: string | null, now: number): string | null => {
  if (!requestedAt) return null;
  const started = new Date(requestedAt).getTime();
  if (Number.isNaN(started)) return null;
  const minutes = Math.floor((now - started) / 60000);
  if (minutes < 1) return '今';
  if (minutes < 60) return `${minutes}分`;
  const hours = Math.floor(minutes / 60);
  return `${hours}時間${minutes % 60}分`;
};

// 注文管理タブを開きっぱなしでも未受領の会計リクエストに気づけるよう、タブより上に常設する sticky バー（ADR 0069）。
// 店舗管理タブのタイル表示（ADR 0068）と違い「どのタブを見ていても目に入る」ことが役割なので、
// 未受領（REQUESTED）だけを出し、受領すると自動的に消える＝残っている分が対応待ちの件数になる。
const CheckoutRequestStatusBar: React.FC<CheckoutRequestStatusBarProps> = ({
  activeTables,
  onSelect,
}) => {
  const theme = useTheme();
  // 経過時間を進めるための時計。バーが出ている間だけ 30 秒ごとに更新する。
  const [now, setNow] = useState(() => Date.now());

  const requested = useMemo(
    () =>
      activeTables
        .filter((table) => table.checkoutRequest?.status === 'REQUESTED')
        // 待たせている順（requestedAt 昇順）。日時が無い行は末尾へ回す。
        .sort((a, b) => {
          const aAt = a.checkoutRequest?.requestedAt ?? '';
          const bAt = b.checkoutRequest?.requestedAt ?? '';
          if (!aAt) return 1;
          if (!bAt) return -1;
          return aAt.localeCompare(bAt);
        }),
    [activeTables]
  );

  const hasRequested = requested.length > 0;

  useEffect(() => {
    if (!hasRequested) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [hasRequested]);

  if (!hasRequested) {
    return null;
  }

  return (
    <Box
      role="region"
      aria-label={`未受領の会計リクエスト ${requested.length}件`}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        px: { xs: 1.5, sm: 2 },
        py: 1,
        boxShadow: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ maxWidth: 'lg', mx: 'auto' }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
          <ReceiptLongIcon fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            会計リクエスト {requested.length}件
          </Typography>
        </Stack>

        {/* 多数同時に来ても sticky が画面を食わないよう、高さは 1 行に固定して横スクロールさせる */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            py: 0.25,
            // スクロールバーで行が太らないように細くする（Firefox / WebKit）
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(0,0,0,0.25)',
              borderRadius: 2,
            },
          }}
        >
          {requested.map((table) => {
            const paymentMethod = table.checkoutRequest?.paymentMethod ?? null;
            const waiting = formatWaiting(table.checkoutRequest?.requestedAt ?? null, now);
            const detail = [paymentMethod, waiting].filter(Boolean).join(' / ');
            return (
              <Chip
                key={table.id}
                label={detail ? `${table.diningTable.name}（${detail}）` : table.diningTable.name}
                onClick={() => onSelect(table.id, table.diningTable.name, paymentMethod)}
                aria-label={`${table.diningTable.name} の会計リクエストを受領する`}
                sx={{
                  flexShrink: 0,
                  fontWeight: 700,
                  bgcolor: 'common.white',
                  color: 'warning.dark',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
};

export default CheckoutRequestStatusBar;
